import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { UiAngleRight } from '@vyaguta/vyaguta-icons/ui';
import type { Column, SubComponentFunction } from 'react-table';

import Table from 'components/common/table';

interface ExpandedRowInfo {
  viewIndex: number;
}

interface NoteAccordionTableProps<D extends object> {
  columns: Array<Column<D>>;
  data: D[];
  loading?: boolean;
  subComponent?: SubComponentFunction;
  showPagination?: boolean;
  emptyMessage?: string;
  customClass?: string;
}

function NoteAccordionTable<D extends object>(props: NoteAccordionTableProps<D>) {
  const { columns, data, loading = false, subComponent, showPagination, emptyMessage, customClass = '' } = props;
  const [draftState, setDraftState] = useState(() => ({
    sourceData: data,
    expanded: {} as Record<number, boolean>,
  }));
  const expanded = draftState.sourceData === data ? draftState.expanded : {};

  const setExpanded = useCallback(
    (value: React.SetStateAction<Record<number, boolean>>) => {
      setDraftState((prev) => {
        const currentExpanded = prev.sourceData === data ? prev.expanded : {};
        const nextExpanded = typeof value === 'function' ? value(currentExpanded) : value;

        return {
          sourceData: data,
          expanded: nextExpanded,
        };
      });
    },
    [data]
  );

  const getColumns = (): Array<Column<D>> => {
    return [
      {
        Header: '',
        sortable: false,
        accessor: 'arrow',
        maxWidth: 40,
        className: 'd-grid align-items-center justify-content-center text-overflow-unset',
        Cell: ({ viewIndex }: ExpandedRowInfo) => (
          <UiAngleRight
            size={20}
            className={classNames('list-accordion__icon', {
              'list-accordion__icon--down': expanded[viewIndex] || false,
              'list-accordion__icon--right': !expanded[viewIndex],
            })}
          />
        ),
      },
    ];
  };

  const expandRow = (row: ExpandedRowInfo | undefined) => {
    if (!row) {
      return;
    }

    setExpanded((currentExpanded) => ({
      ...currentExpanded,
      [row.viewIndex]: !currentExpanded[row.viewIndex],
    }));
  };
  return (
    <div className={`note-accordion-table ${customClass}`}>
      <Table<D>
        columns={[...getColumns(), ...columns]}
        data={data}
        loading={loading}
        expanded={expanded}
        getTdProps={(_, rowInfo) => {
          return {
            onClick: () => {
              expandRow(rowInfo as ExpandedRowInfo | undefined);
            },
          };
        }}
        getTrProps={(_, rowInfo) => {
          const expandedRow = rowInfo as ExpandedRowInfo | undefined;

          return {
            className: classNames('list-accordion__row', {
              'list-accordion__row--expanded': expanded[expandedRow?.viewIndex ?? -1] || false,
            }),
          };
        }}
        subComponent={subComponent}
        showPagination={showPagination}
        noDataText={emptyMessage}
        sortable={false}
      />
    </div>
  );
}

export default NoteAccordionTable;
